import { FC, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid-pro';

import { MasterIndexApi } from '@/api';
import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { useToastStore } from '@/store';
import { openNewWindow } from '@/utils';

import { cfsHistoryDataGridCols } from '../data';

interface CFSHistoryDataGridProps {
  addressId: string;
  currentCfsId?: string;
}

const CFSHistoryDataGrid: FC<CFSHistoryDataGridProps> = ({
  addressId,
  currentCfsId,
}) => {
  const { updateToast } = useToastStore();
  const navigate = useNavigate();
  const [refreshKey] = useState(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    cfsHistoryDataGridCols,
  );

  const handleViewCFS = (cfsId: string) => {
    const displayOption =
      localStorage.getItem('cfs-display-option') || 'slide-view';

    if (displayOption === 'new-window') {
      openNewWindow(`/cfs/${cfsId}`, cfsId);
    } else {
      navigate(`/cfs/${cfsId}`);
    }
  };

  const fetchCFSHistory = useCallback(
    async (
      page: number,
      limit: number,
      filter: GridFilterModel | string,
    ): Promise<{ rows: any[]; totalCount: number }> => {
      try {
        // Use the master address related records endpoint
        const response = await MasterIndexApi.getAddressRelatedRecords(
          addressId,
          {
            limit: 1000,
            sort: JSON.stringify({ createdAt: -1 }),
          },
        );

        // Handle different response structures
        let records = [];
        if (Array.isArray(response.data)) {
          records = response.data;
        } else if (response.data.docs) {
          records = response.data.docs;
        } else if (response.data.results) {
          records = response.data.results;
        } else {
          return { rows: [], totalCount: 0 };
        }

        // Filter for CFS records only (exclude cases) and exclude current CFS
        let cfsRecords = records.filter(
          (record: any) =>
            record.name &&
            record.type &&
            record.type === 'CFS' &&
            record._id !== currentCfsId,
        );

        // Transform the data to match CFS history grid format
        cfsRecords = cfsRecords.map((record: any) => {
          // Extract incident codes and create description
          const incidentCodes = record.incidentCodes || [];
          const shortDescription = incidentCodes
            .map((ic: any) => ic.description)
            .join(', ');

          return {
            _id: record._id,
            number: record.name,
            createdAt: record.createdAt,
            incidentCodes, // Keep as array for columns to handle
            shortDescription: shortDescription || 'No Description',
            address: record.address,
            location: record.address?.addressLabel || 'No Address',
          };
        });

        cfsRecords = cfsRecords.filter(
          (record: any) => record._id && record.number && record.createdAt,
        );

        const filterObj =
          typeof filter === 'string' && filter
            ? JSON.parse(filter)
            : (filter as GridFilterModel) || {
                items: [],
                quickFilterValues: [],
              };

        if (
          filterObj.quickFilterValues &&
          filterObj.quickFilterValues.length > 0
        ) {
          const searchTerm = filterObj.quickFilterValues[0].toLowerCase();
          cfsRecords = cfsRecords.filter((record: any) => {
            const incidentCodesText =
              record.incidentCodes
                ?.map((ic: any) => `${ic.code} ${ic.description}`)
                .join(' ')
                .toLowerCase() || '';

            return (
              record.number?.toLowerCase().includes(searchTerm) ||
              record.location?.toLowerCase().includes(searchTerm) ||
              incidentCodesText.includes(searchTerm) ||
              record.shortDescription?.toLowerCase().includes(searchTerm)
            );
          });
        }

        // Sort by creation date (newest first)
        cfsRecords.sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedRecords = cfsRecords.slice(startIndex, endIndex);

        // Update total records count and loading state
        if (page === 1) {
          setTotalRecords(cfsRecords.length);
          setIsLoading(false);
        }

        return {
          rows: paginatedRecords,
          totalCount: cfsRecords.length,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        setTotalRecords(0);
        setIsLoading(false);
        return { rows: [], totalCount: 0 };
      }
    },
    [addressId, currentCfsId, updateToast],
  );

  // Trigger initial data fetch to determine if there are records
  useEffect(() => {
    if (addressId) {
      // Call fetchCFSHistory with initial parameters to set loading state
      fetchCFSHistory(1, 10, '').then(() => {
        // Initial fetch completed
      });
    }
  }, [addressId, currentCfsId, fetchCFSHistory]);

  const columns = useDataGridColumns({
    type: 'cfsHistory',
    columnSettings,
    onView: handleViewCFS,
  });

  // Show loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={200}
      >
        Loading...
      </Box>
    );
  }

  // Show message when no records found
  if (totalRecords === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={200}
        textAlign="center"
      >
        <Box>No previous incidents found for this address.</Box>
      </Box>
    );
  }

  // Show data grid when records exist
  return (
    <Box>
      <Box height={400} width="100%">
        <DataGrid
          key={refreshKey}
          name="cfs-history"
          fetchData={fetchCFSHistory}
          columnSettings={columnSettings}
          setColumnSettings={setColumnSettings}
          columns={columns}
          hideFilter={false}
          disableRowSelectionOnClick
          autoPageSize={false}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10]}
        />
      </Box>
    </Box>
  );
};

export default CFSHistoryDataGrid;
